import { type JSX } from "react";
import type { BeatResponse } from "../../api/beats";
import { useBeatTimeline } from "./useBeatTimeline";

/**
 * scene-beat-decomposition (Phase 5+ Tier 5G 第 1 段) Phase 5.1 reader UI:
 * 現在 scene が属する Beat function を 1 行 indicator で表示する placement-agnostic
 * component。 reader UI のヘッダ / フッタ / overlay 等の任意位置に mount 可能。
 *
 * 配置: standalone reusable (= sibling BeatTimelineView と同 doctrine)。 production 配線は
 * 別 PR (= reader UI 側で optional 表示 toggle 経由)、 base scope は dormant 配備。
 *
 * 解決経路: GET /scenarios/:id/beats/timeline で全 Beat + 紐付け scene 情報を取得し、
 * **sceneIds に最新 sceneId を含む Beat** を current として表示する。 caller が
 * `currentSceneId` を指定しない場合は **order 最大の Beat** を fallback (= scenario 全体の最後の
 * Beat、 ending 表示用途として妥当)。 Beat 未設定 / fetch 失敗で null を render (= 表示自体を
 * 抑制、 reader UX を破壊しない silent fallback)。
 *
 * 設計対応:
 *   - design.md §Phase 5: reader UI
 *   - requirements.md Phase 5.1
 */
export interface CurrentBeatIndicatorProps {
  baseUrl: string;
  scenarioId: string;
  /**
   * 現在 reader が読んでいる scene の id。 指定時は本 sceneId を含む Beat を表示。
   * 未指定で order 最大の Beat にフォールバック (= scenario 全体の最終 beat)。
   */
  currentSceneId?: string;
  /** 関数名 → 表示名 override map (= sibling BeatTimelineView と同 prop)。 */
  functionLabelMap?: Partial<Record<string, string>>;
}

const DEFAULT_FUNCTION_LABELS: Record<string, string> = {
  setup: "状況提示",
  inciting: "事件発生",
  rising: "上昇",
  climax: "頂点",
  falling: "下降",
  denouement: "終結",
};

export function CurrentBeatIndicator({
  baseUrl,
  scenarioId,
  currentSceneId,
  functionLabelMap,
}: CurrentBeatIndicatorProps): JSX.Element | null {
  // shared hook 経由で fetch (= sibling BeatTimelineView と統一)。 reader UX 破壊しない方針で
  // error は silent fallback (= null render)、 entries 0 件 / fetch 未完了でも null render。
  const { entries, error } = useBeatTimeline(baseUrl, scenarioId);

  if (error !== null || entries === null || entries.length === 0) return null;

  // currentSceneId 指定時は sceneIds に含む Beat を優先、 未指定 / 不在で order 最大 Beat に fallback。
  const match =
    currentSceneId !== undefined
      ? entries.find((entry) => entry.sceneIds.includes(currentSceneId))
      : undefined;
  const fallback = entries[entries.length - 1];
  const currentBeat: BeatResponse | null =
    match?.beat ?? fallback?.beat ?? null;

  if (currentBeat === null) return null;

  const label =
    currentBeat.customLabel !== null && currentBeat.customLabel !== ""
      ? currentBeat.customLabel
      : (functionLabelMap?.[currentBeat.functionType] ??
        DEFAULT_FUNCTION_LABELS[currentBeat.functionType] ??
        currentBeat.functionType);

  return (
    <div
      data-testid="current-beat-indicator"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="text-reader-secondary text-xs flex items-baseline gap-1"
      aria-label={`現在の物語段階: ${label}`}
    >
      <span className="opacity-70">ビート:</span>
      <span>{label}</span>
    </div>
  );
}
